import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/lib/query-client'
import * as turnosApi from '@/lib/api/turnos'
import type { TurnoIn, PagoReservaIn } from '@/types/api'

function invalidateTurnosAndPacientes(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: queryKeys.turnos.all })
  qc.invalidateQueries({ queryKey: queryKeys.pacientes.all })
}

export function useTurnosDia(sedeId: string, fecha: string) {
  return useQuery({
    queryKey: ['turnos', 'dia', sedeId, fecha],
    queryFn: async () => {
      const res = await turnosApi.list({ sede_id: sedeId, desde: fecha, hasta: fecha })
      return Array.isArray(res) ? res : (res.items ?? [])
    },
    enabled: !!sedeId && !!fecha,
    staleTime: 30_000,
  })
}


export function useTurnos(params?: Parameters<typeof turnosApi.list>[0]) {
  return useQuery({
    queryKey: queryKeys.turnos.list(params),
    queryFn: async () => {
      const res = await turnosApi.list(params)
      return res
    },
    staleTime: 30_000,
  })
}

export function useTurnosPaciente(pacienteDni: string) {
  return useQuery({
    queryKey: ['turnos', 'paciente', pacienteDni],
    queryFn: async () => {
      const res = await turnosApi.list({ paciente_dni: pacienteDni })
      // list() devuelve PaginatedResponse — extraemos el array de items
      return Array.isArray(res) ? res : (res.items ?? [])
    },
    enabled: !!pacienteDni,
    staleTime: 30_000,
  })
}

export function useCreateTurno() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: TurnoIn) => turnosApi.create(data),
    onSuccess: () => {
      invalidateTurnosAndPacientes(qc)
    },
  })
}

export function useUpdateTurno() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<TurnoIn> }) =>
      turnosApi.update(id, data),
    onSuccess: (_result, { id }) => {
      invalidateTurnosAndPacientes(qc)
      qc.invalidateQueries({ queryKey: queryKeys.turnos.detail(id) })
    },
  })
}

export function useCompletarTurno() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => turnosApi.completar(id),
    onSuccess: () => {
      invalidateTurnosAndPacientes(qc)
    },
  })
}

export function usePagarReserva() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: PagoReservaIn }) =>
      turnosApi.pagarReserva(id, data),
    onSuccess: () => {
      invalidateTurnosAndPacientes(qc)
    },
  })
}

export function useReservarTurno(pacienteDni?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (turnoId: string) => turnosApi.reservarTurno(turnoId),
    onSuccess: () => {
      invalidateTurnosAndPacientes(qc)
      if (pacienteDni) {
        qc.invalidateQueries({ queryKey: queryKeys.pacientes.detail(pacienteDni) })
      }
    },
  })
}

export function useDeleteTurno() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => turnosApi.remove(id),
    onSuccess: () => {
      invalidateTurnosAndPacientes(qc)
    },
  })
}
